import React, { useState, useEffect } from "react";
import { ConfigProvider, Layout, message } from "antd";
import type { ClusterEnvItem } from "./api";
import * as api from "./api";
import { darkTheme } from "./theme/darkTheme";
import { useHistoryRecordTasks } from "./hooks/useHistoryRecordTasks";
import { useJsonModal } from "./hooks/useJsonModal";
import { useRecordLogs } from "./hooks/useRecordLogs";
import { handleCopyText } from "./utils/textUtils";
import Header from "./components/Header";
import Content from "./components/Content";
import JsonModal from "./components/JsonModal";
import HistoryRecordTasks from "./components/HistoryRecordTasks";
import ClusterManagement from "./components/ClusterManagement";
import ReplayResults from "./components/ReplayResults";

/**
 * 应用程序根组件
 * @returns React 元素
 */
const App: React.FC = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const isReplayResultPage =
    urlParams.get("mode") === "replay" || !!urlParams.get("taskId");

  // --- 环境和业务线状态 ---
  const [clusterEnvItems, setClusterEnvItems] = useState<ClusterEnvItem[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<string | undefined>(
    undefined
  );
  const [selectedEnv, setSelectedEnv] = useState<string | undefined>(undefined);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [ignoreInterval, setIgnoreInterval] = useState<boolean>(false);
  const [replayTarget, setReplayTarget] = useState<string>("");
  const [clusterModalVisible, setClusterModalVisible] = useState<boolean>(false);

  // --- 自定义 Hooks ---
  const {
    bodyVisible,
    responseVisible,
    headerVisible,
    modalContent,
    showJsonModal,
    hideBodyModal,
    hideResponseModal,
    hideHeaderModal,
  } = useJsonModal();

  const {
    logData,
    loadingLogs,
    pagination,
    taskIdFilter,
    setTaskIdFilter,
    requestIdFilter,
    setRequestIdFilter,
    requestHeaderFilter,
    setRequestHeaderFilter,
    statusFilter,
    setStatusFilter,
    fetchLogs,
    handleTableChange,
    handleApplyFilter,
  } = useRecordLogs();

  const {
    historyModalVisible,
    historyLoading,
    historyData,
    historyPagination,
    fetchHistoryTasks,
    handleShowHistoryModal,
    handleCloseHistoryModal,
    handleHistoryTableChange,
    handleStopHistoryTask,
  } = useHistoryRecordTasks(
    selectedEnv,
    isRecording,
    setIsRecording,
    setCurrentTaskId
  );

  // --- 数据获取 ---
  const fetchEnvs = async () => {
    try {
      const res = await api.listClusterEnvs();
      if (res.data && Array.isArray(res.data.items)) {
        setClusterEnvItems(res.data.items);
      } else {
        setClusterEnvItems([]);
      }
    } catch (error) {
      message.error("获取环境列表失败");
      setClusterEnvItems([]);
    }
  };

  useEffect(() => {
    fetchEnvs();
  }, []);

  // 初始加载日志
  useEffect(() => {
    fetchLogs(1, 10, {
      taskId: taskIdFilter,
      requestId: requestIdFilter,
      requestHeader: requestHeaderFilter,
      status: statusFilter,
    });
  }, []);

  // --- 事件处理函数 ---
  const handleBusinessEnvChange = (
    business: string | undefined,
    env: string | undefined
  ) => {
    setSelectedBusiness(business);
    setSelectedEnv(env);
    setCurrentTaskId(null);
    setIsRecording(false);
    setTaskIdFilter("");
    setRequestIdFilter("");
    setRequestHeaderFilter("");
    setStatusFilter("");
  };

  const handleStartRecording = async () => {
    if (!selectedEnv) {
      message.warning("请先选择环境");
      return;
    }
    if (!selectedBusiness) {
      message.warning("请先选择产线");
      return;
    }
    setIsRecording(true);
    try {
      await api.createRecordTask({
        cluster_env: selectedEnv,
        business: selectedBusiness,
      });
      message.success(`环境 ${selectedEnv} 开始录制成功`);
    } catch (error) {
      message.error("开始录制失败");
      console.error(error);
      setIsRecording(false);
    }
  };

  const handleStopRecording = async () => {
    if (!selectedEnv) {
      message.warning("未选择环境");
      return;
    }
    setIsRecording(false);
    try {
      await api.pauseRecordTask({ cluster_env: selectedEnv });
      message.success(`环境 ${selectedEnv} 停止录制成功`);
      setCurrentTaskId(null);
    } catch (error) {
      message.error("停止录制失败");
      console.error(error);
      setIsRecording(true);
    }
  };

  const handleCopyCurl = async (requestId: string) => {
    try {
      const res = await api.getReplayRequestCurl({ request_id: requestId });
      handleCopyText(res.data?.command, "cURL 命令");
    } catch (error) {
      message.error("获取 cURL 命令失败");
      console.error(error);
    }
  };

  const handleReplayRequest = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning("请至少选择一个请求进行重放");
      return;
    }
    if (!selectedEnv) {
      message.warning("请先选择一个环境");
      return;
    }

    const requestIds = selectedRowKeys.map((key) => String(key));

    try {
      const requestData: api.CreateReplayTaskRequest = {
        request_ids: requestIds,
        cluster_env: selectedEnv,
        skip_duration: ignoreInterval,
        rewrite: replayTarget.trim() || undefined,
      };
      const res = (await api.createReplayTask(requestData)) as {
        message: string;
      };
      message.success(
        `已为环境 ${selectedEnv} 创建 ${requestIds.length} 个请求的回放任务${
          ignoreInterval ? " (忽略间隔)" : ""
        }${replayTarget.trim() ? `，目标：${replayTarget.trim()}` : ""}`
      );
      setSelectedRowKeys([]);
      const url = new URLSearchParams(window.location.search);
      !url.get("taskId") &&
        window.open(
          `${window.location.pathname}?mode=replay&task_id=${res.message}`,
          "_blank"
        );
    } catch (error) {
      message.error("创建回放请求失败");
      console.error(error);
    }
  };

  return (
    <ConfigProvider theme={darkTheme}>
      <Layout style={{ minHeight: "100vh" }}>
        <Header
          clusterEnvItems={clusterEnvItems}
          isRecording={isRecording}
          selectedEnv={selectedEnv}
          onBusinessEnvChange={handleBusinessEnvChange}
          onStartRecording={handleStartRecording}
          onStopRecording={handleStopRecording}
          onShowHistory={handleShowHistoryModal}
          onShowClusters={() => setClusterModalVisible(true)}
          onShowReplayResults={() =>
            window.open(`${window.location.pathname}?mode=replay`, "_blank")
          }
        />

        {isReplayResultPage ? (
          <ReplayResults />
        ) : (
          <>
            <Content
          logData={logData}
          loadingLogs={loadingLogs}
          pagination={pagination}
          selectedRowKeys={selectedRowKeys}
          taskIdFilter={taskIdFilter}
          requestIdFilter={requestIdFilter}
          requestHeaderFilter={requestHeaderFilter}
          statusFilter={statusFilter}
          ignoreInterval={ignoreInterval}
          replayTarget={replayTarget}
          onTaskIdFilterChange={(e) => setTaskIdFilter(e.target.value)}
          onRequestIdFilterChange={(e) => setRequestIdFilter(e.target.value)}
          onRequestHeaderFilterChange={(e) =>
            setRequestHeaderFilter(e.target.value)
          }
          onStatusFilterChange={(e) => setStatusFilter(e.target.value)}
          onApplyFilter={handleApplyFilter}
          onTableChange={handleTableChange}
          onRowSelectionChange={setSelectedRowKeys}
          onShowJsonModal={showJsonModal}
          onCopyCurl={handleCopyCurl}
          onCopyText={handleCopyText}
          onIgnoreIntervalChange={(e) => setIgnoreInterval(e.target.checked)}
          onReplayTargetChange={setReplayTarget}
          onReplayRequest={handleReplayRequest}
        />

            <JsonModal
          title="Request Body"
          visible={bodyVisible}
          content={modalContent}
          onCancel={hideBodyModal}
            />

            <JsonModal
          title="Request Header"
          visible={headerVisible}
          content={modalContent}
          onCancel={hideHeaderModal}
            />

            <JsonModal
          title="Response Body"
          visible={responseVisible}
          content={modalContent}
          onCancel={hideResponseModal}
            />
          </>
        )}

        <HistoryRecordTasks
          open={historyModalVisible}
          loading={historyLoading}
          data={historyData}
          pagination={historyPagination}
          onCancel={handleCloseHistoryModal}
          onPageChange={(page, pageSize) => fetchHistoryTasks(page, pageSize)}
          onStopTask={handleStopHistoryTask}
          onCopyTaskId={(taskId: string) => handleCopyText(taskId, "Task ID")}
        />

        <ClusterManagement
          open={clusterModalVisible}
          onCancel={() => setClusterModalVisible(false)}
          onClustersChanged={fetchEnvs}
        />
      </Layout>
    </ConfigProvider>
  );
};

export default App;
